import React, { useRef } from "react";
import FileExplorer from "../FileExplorer";
import CodeEditor from "../CodeEditor";
import { AIChat } from "../../aiChat";
import ErrorBoundary from "../ErrorBoundary";
import {
  getPanelHeaderConfig,
  ClearChatIcon,
  HidePanelIcon,
} from "./panelIcons";

function CodePanel({
  webViewMode,
  isSmallScreen,
  isMobileViewport,
  sidePanelView,
  setSidePanelView,
  isFileExplorerVisible,
  setIsFileExplorerVisible,
  showCodeEditor,
  panelMode,
  onHidePanel,
  aiEnabled,
  fileList,
  setFileList,
  currentFile,
  code,
  codeLanguage,
  updateCode,
  saveFileContent,
  switchFile,
  handleCreateFile,
  handleAIFilesChanged,
  setIsPickerActive,
  setHasChanged,
  domain,
  dbRef,
  iframeRef,
  editorRef,
  onAIPaywallRequired,
}) {
  const aiChatRef = useRef(null);
  const effectivePanelMode = aiEnabled
    ? panelMode
    : panelMode === "ai"
      ? "code"
      : panelMode;
  const effectiveSidePanelView = aiEnabled
    ? sidePanelView
    : sidePanelView === "ai"
      ? "code"
      : sidePanelView;
  const headerConfig = getPanelHeaderConfig({
    panelMode: effectivePanelMode,
    sidePanelView: effectiveSidePanelView,
    showCodeEditor,
  });
  const showSidePanelHeader = !(
    isMobileViewport && effectiveSidePanelView === "code"
  );

  return (
    <div
      className={`codemode ${!webViewMode ? "open" : ""} ${isSmallScreen ? "show-on-small-screen" : ""}`}
    >
      {/* Side Panel Header */}
      {showSidePanelHeader && (
        <div className="side-panel-header">
          <div className="side-panel-title">
            <span className="side-panel-title-icon">{headerConfig.icon}</span>
            <span className="side-panel-title-text">{headerConfig.label}</span>
            {(effectivePanelMode || effectiveSidePanelView) === "ai" && (
              <span className="side-panel-beta-label">BETA</span>
            )}
          </div>
          <div className="side-panel-header-actions">
            {aiEnabled &&
              (effectivePanelMode || effectiveSidePanelView) === "ai" && (
                <>
                  <button
                    type="button"
                    className="side-panel-action-btn"
                    onClick={() => aiChatRef.current?.clearChat()}
                    aria-label="Clear chat"
                    title="Clear Chat"
                  >
                    <ClearChatIcon />
                  </button>
                </>
              )}
            <button
              type="button"
              className="side-panel-hide-btn"
              onClick={onHidePanel}
              aria-label="Hide panel"
            >
              <HidePanelIcon />
            </button>
          </div>
        </div>
      )}

      {/* Side Panel Content */}
      <div className="side-panel-content">
        {/* Code View */}
        <div
          className={`side-panel-view side-panel-view-code ${effectiveSidePanelView === "code" ? "active" : ""}`}
        >
          {isFileExplorerVisible && (
            <ErrorBoundary>
              <FileExplorer
                isFileExplorerVisible={isFileExplorerVisible}
                fileList={fileList}
                onFileClick={switchFile}
                onCreateFile={handleCreateFile}
                filename={currentFile}
              />
            </ErrorBoundary>
          )}
          {showCodeEditor && (
            <ErrorBoundary>
              <CodeEditor
                filename={currentFile}
                language={codeLanguage}
                code={code}
                setHasChanged={setHasChanged}
                setCode={updateCode}
                setWebMode={() => {}}
                setIsFileExplorerVisible={setIsFileExplorerVisible}
                ref={editorRef}
                saveFile={saveFileContent}
                iframeRef={iframeRef}
              />
            </ErrorBoundary>
          )}
        </div>

        {/* AI View */}
        <div
          className={`side-panel-view side-panel-view-ai ${effectiveSidePanelView === "ai" ? "active" : ""}`}
        >
          {aiEnabled && (
            <ErrorBoundary>
              <AIChat
                ref={aiChatRef}
                domain={domain}
                filename={currentFile}
                fileList={fileList}
                setFileList={setFileList}
                dbRef={dbRef}
                iframeRef={iframeRef}
                saveFile={saveFileContent}
                onPickerStateChange={setIsPickerActive}
                onFilesChanged={handleAIFilesChanged}
                onPaywallRequired={onAIPaywallRequired}
                sidePanel={true}
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(CodePanel);
